import React, {useEffect, useState, useRef} from 'react';
import type {FormField} from '../../../types/form-builder.types';
import {Lock, ShieldCheck, AlertCircle} from 'lucide-react';
import {cn, Alert, AlertDescription, useLanguage} from '@shondalai/ui-commons';
import {useFormSubmission} from '../../../contexts/form-submission-context';
import {countries} from '../../../data/countries';

// Stripe types
declare global {
  interface Window {
    Stripe: any;
    __stripePaymentElements?: {
      [fieldId: string]: {
        cardElement: any;
        stripe: any;
      };
    };
  }
}

interface PaymentFieldProps {
  field: FormField;
  value?: any;
  onChange?: (value: any) => void;
  error?: string;
  formData?: Record<string, any>;
}

/**
 * Evaluate calculation formula
 * Replaces {field_name} with actual values from formData
 */
const evaluateFormula = (formula: string, formData: Record<string, any>): number => {
  try {
    let evaluatedFormula = formula;

    // Replace all {field_name} with actual values
    const matches = formula.match(/\{([^}]+)\}/g);
    if (matches) {
      matches.forEach(match => {
        const fieldName = match.slice(1, -1); // Remove { }
        const value = formData[fieldName] || 0;
        const numValue = parseFloat(value) || 0;
        evaluatedFormula = evaluatedFormula.replace(match, numValue.toString());
      });
    }

    // Safely evaluate the mathematical expression
    // eslint-disable-next-line no-eval
    const result = eval(evaluatedFormula);
    return isNaN(result) ? 0 : parseFloat(result);
  } catch (error) {
    console.error('Formula evaluation error:', error);
    return 0;
  }
};

/**
 * Functional Payment Field Component
 * Integrates with Stripe for actual payment processing
 */
export const PaymentField: React.FC<PaymentFieldProps> = ({
  field,
  onChange,
  error,
  formData = {}
}) => {
  const { t } = useLanguage();
  const {config} = field;
  const label = config.label as string | undefined;
  const required = config.required as boolean | undefined;

  // Get integration config from context based on gateway
  const {getIntegrationConfig} = useFormSubmission();
  const paymentGateway = config.paymentGateway || 'stripe';
  const stripeConfig = paymentGateway === 'stripe' ? getIntegrationConfig('stripe') : null;
  const paypalConfig = paymentGateway === 'paypal' ? getIntegrationConfig('paypal') : null;

  // Payment configuration
  const amountType = config.amountType || 'fixed';
  const fixedAmount = parseFloat(config.amount as any) || 0;
  const minAmount = parseFloat(config.minAmount as any) || 0;
  const maxAmount = parseFloat(config.maxAmount as any) || 10000;
  const calculationFormula = config.calculationFormula as string | undefined;
  const currency = config.currency || 'USD';
  const currencySymbol = config.currencySymbol || '$';
  const testMode = paymentGateway === 'stripe' 
    ? (stripeConfig?.test_mode !== false) 
    : (paypalConfig?.sandbox_mode !== false);

  // UI configuration
  const showPaymentSummary = config.showPaymentSummary !== false;
  const showSecurityBadge = config.showSecurityBadge !== false;
  const compactMode = config.compactMode || false;
  const collectBillingAddress = config.collectBillingAddress !== false;
  const requireBillingAddress = config.requireBillingAddress !== false;

  // Tax and fees
  const taxEnabled = config.taxEnabled || false;
  const taxRate = parseFloat(config.taxRate as any) || 0;
  const taxLabel = config.taxLabel || 'Tax';
  const processingFeeEnabled = config.processingFeeEnabled || false;
  const processingFeeType = config.processingFeeType || 'percentage';
  const processingFee = parseFloat(config.processingFee as any) || 0;
  const passProcessingFee = config.passProcessingFee || false;

  // State
  const [userAmount, setUserAmount] = useState<string>('');
  const [cardholderName, setCardholderName] = useState('');
  const [billingAddress, setBillingAddress] = useState({
    street: '',
    city: '',
    state: '',
    zip: '',
    country: 'US'
  });

  // Stripe state
  const [stripe, setStripe] = useState<any>(null);
  const [cardElement, setCardElement] = useState<any>(null);
  const [cardComplete, setCardComplete] = useState(false);
  const [cardError, setCardError] = useState<string>('');
  const [stripeLoading, setStripeLoading] = useState(true);
  const [stripeInitError, setStripeInitError] = useState<string>('');
  const cardElementRef = useRef<HTMLDivElement>(null);

  // Store card element and stripe instance in window for access during payment confirmation
  // This avoids circular reference issues when serializing form data
  useEffect(() => {
    if (cardElement && stripe) {
      if (!window.__stripePaymentElements) {
        window.__stripePaymentElements = {};
      }
      window.__stripePaymentElements[field.id] = {
        cardElement,
        stripe,
      };
    }

    return () => {
      if (window.__stripePaymentElements && window.__stripePaymentElements[field.id]) {
        delete window.__stripePaymentElements[field.id];
      }
    };
  }, [cardElement, stripe, field.id]);

  // Initialize Stripe (only if Stripe is selected)
  useEffect(() => {
    // Skip if PayPal is selected
    if (paymentGateway !== 'stripe') {
      setStripeLoading(false);
      return;
    }

    const initStripe = async () => {
      try {
        setStripeLoading(true);
        setStripeInitError('');

        // Check if Stripe integration is configured
        if (!stripeConfig) {
          // Wait a bit for integrations to load
          await new Promise(resolve => setTimeout(resolve, 500));

          // Check again after waiting
          const retryConfig = getIntegrationConfig('stripe');
          if (!retryConfig) {
            setStripeInitError(
              'Stripe integration not configured. Please add and activate a Stripe integration in the Integrations page.'
            );
            setStripeLoading(false);
            return;
          }

          // Use the retry config
          const publishableKey = testMode
            ? retryConfig.test_publishable_key
            : retryConfig.publishable_key;

          if (!publishableKey) {
            setStripeInitError(
              'Stripe publishable key not configured. Please add your ' +
              (testMode ? 'test' : 'live') + ' publishable key in the Stripe integration settings.'
            );
            setStripeLoading(false);
            return;
          }

          // Continue with initialization using retry config
          await initializeStripeElement(publishableKey);
          return;
        }

        // Get publishable key from integration config
        const publishableKey = testMode
          ? stripeConfig.test_publishable_key
          : stripeConfig.publishable_key;

        if (!publishableKey) {
          setStripeInitError(
            'Stripe publishable key not configured. Please add your ' +
            (testMode ? 'test' : 'live') + ' publishable key in the Stripe integration settings.'
          );
          setStripeLoading(false);
          return;
        }

        await initializeStripeElement(publishableKey);

      } catch (error) {
        console.error('Stripe initialization error:', error);
        setStripeInitError('Failed to initialize payment form. Please refresh the page.');
        setStripeLoading(false);
      }
    };

    const initializeStripeElement = async (publishableKey: string) => {
      // Dynamically load Stripe.js if not already loaded
      if (typeof window.Stripe === 'undefined') {
        // Check if script is already being loaded or exists
        const existingScript = document.querySelector('script[src="https://js.stripe.com/v3/"]');
        
        if (!existingScript) {
          const script = document.createElement('script');
          script.src = 'https://js.stripe.com/v3/';
          script.async = true;

          // Wait for script to load
          await new Promise<void>((resolve, reject) => {
            script.onload = () => resolve();
            script.onerror = () => reject(new Error('Failed to load Stripe.js'));
            document.head.appendChild(script);
          });
        } else {
          // Script exists, wait for it to load
          await new Promise<void>((resolve) => {
            const checkStripe = setInterval(() => {
              if (typeof window.Stripe !== 'undefined') {
                clearInterval(checkStripe);
                resolve();
              }
            }, 100);
            // Timeout after 5 seconds
            setTimeout(() => {
              clearInterval(checkStripe);
              resolve();
            }, 5000);
          });
        }
      }

      // Wait a bit more for Stripe to initialize
      let attempts = 0;
      while (typeof window.Stripe === 'undefined' && attempts < 20) {
        await new Promise(resolve => setTimeout(resolve, 100));
        attempts++;
      }

      if (typeof window.Stripe === 'undefined') {
        setStripeInitError('Stripe.js failed to load. Please refresh the page.');
        setStripeLoading(false);
        return;
      }

      const stripeInstance = window.Stripe(publishableKey);
      setStripe(stripeInstance);

      const elements = stripeInstance.elements();
      const card = elements.create('card', {
        style: {
          base: {
            fontSize: '16px',
            color: '#424770',
            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
            '::placeholder': {
              color: '#aab7c4',
            },
          },
          invalid: {
            color: '#9e2146',
          },
        },
        hidePostalCode: true,
      });

      // Wait for DOM to be ready
      if (cardElementRef.current) {
        card.mount(cardElementRef.current);

        card.on('change', (event: any) => {
          setCardComplete(event.complete);
          setCardError(event.error ? event.error.message : '');
        });

        card.on('ready', () => {
          setStripeLoading(false);
        });

        setCardElement(card);
      } else {
        setStripeInitError('Card element container not ready');
        setStripeLoading(false);
      }
    };

    initStripe();

    return () => {
      if (cardElement) {
        cardElement.destroy();
      }
    };
  }, [testMode, stripeConfig, getIntegrationConfig, paymentGateway]);

  // Calculate amounts
  const getAmount = () => {
    if (amountType === 'user-defined') {
      return parseFloat(userAmount) || 0;
    } else if (amountType === 'calculated' && calculationFormula) {
      return evaluateFormula(calculationFormula, formData);
    }
    return fixedAmount;
  };

  const baseAmount = getAmount();
  const taxAmount = taxEnabled ? (baseAmount * taxRate) / 100 : 0;
  const feeAmount = processingFeeEnabled && passProcessingFee
    ? processingFeeType === 'percentage'
      ? (baseAmount * processingFee) / 100
      : processingFee
    : 0;
  const totalAmount = baseAmount + taxAmount + feeAmount;

  // Simple validation - check if payment is ready
  const isPaymentValid = () => {
    // Check amount
    if (totalAmount <= 0) {
      return { valid: false, message: 'Payment amount must be greater than zero' };
    }

    // For Stripe, check card completion
    if (paymentGateway === 'stripe') {
      if (!cardComplete) {
        return { valid: false, message: 'Please complete your card details (card number, expiry date, and CVV)' };
      }
    }

    // For PayPal, payment will be processed on PayPal's site (no local validation needed)

    // Check billing address if required
    if (requireBillingAddress && collectBillingAddress) {
      if (!billingAddress.street || !billingAddress.city || !billingAddress.zip) {
        return { valid: false, message: 'Please complete all required billing address fields' };
      }
    }

    return { valid: true, message: '' };
  };

  // Update parent component
  useEffect(() => {
    if (onChange) {
      const isBillingValid = !requireBillingAddress || !collectBillingAddress || (
        billingAddress.street &&
        billingAddress.city &&
        billingAddress.zip
      );

      // For Stripe: require card completion
      // For PayPal: no card needed (payment on PayPal site)
      const isCardValid = paymentGateway === 'paypal' ? true : cardComplete;
      const isValid = totalAmount > 0 && isCardValid && isBillingValid;

      onChange({
        amount: totalAmount,
        baseAmount,
        taxAmount,
        feeAmount,
        currency,
        gateway: paymentGateway,
        cardholderName,
        billingAddress: collectBillingAddress ? billingAddress : undefined,
        ready: isValid,
        _fieldId: field.id,
        validate: isPaymentValid, // Pass validation function
      });
    }
  }, [totalAmount, baseAmount, taxAmount, feeAmount, cardholderName, billingAddress, cardComplete, paymentGateway, requireBillingAddress, collectBillingAddress, currency, onChange, field.id]);

  return (
    <div className="space-y-4" data-field-id={field.id}>
      {/* Field Label */}
      {label && (
        <label className="easyforms-field-label">
          {label}
          {required && <span className="easyforms-field-required">*</span>}
        </label>
      )}

      {/* Error Alert */}
      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Payment Summary */}
      {showPaymentSummary && (
        <div
          className="rounded-lg p-4"
          style={{
            border: '1px solid var(--ef-border, #E2E8F0)',
            background: 'var(--ef-surface, #F8FAFC)'
          }}
        >
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span
                className="text-sm font-medium"
                style={{ color: 'var(--ef-text, #1E293B)' }}
              >
                {amountType === 'user-defined' && t('COM_EASYFORMS_ENTER_AMOUNT', 'Enter Amount')}
                {amountType === 'fixed' && t('COM_EASYFORMS_AMOUNT', 'Amount')}
                {amountType === 'calculated' && t('COM_EASYFORMS_CALCULATED_AMOUNT', 'Calculated Amount')}
              </span>
              <span
                className="text-lg font-bold"
                style={{ color: 'var(--ef-primary, #0F172A)' }}
              >
                {amountType === 'user-defined' ? (
                  <input
                    type="number"
                    placeholder={`${currencySymbol}0.00`}
                    value={userAmount}
                    onChange={(e) => setUserAmount(e.target.value)}
                    min={minAmount}
                    max={maxAmount}
                    step="0.01"
                    className="easyforms-field-input w-32 text-right"
                  />
                ) : (
                  `${currencySymbol}${baseAmount.toFixed(2)}`
                )}
              </span>
            </div>

            {amountType === 'calculated' && calculationFormula && (
              <p
                className="text-xs"
                style={{ color: 'var(--ef-textSecondary, #64748B)' }}
              >
                {t('COM_EASYFORMS_FORMULA', 'Formula')}: {calculationFormula}
              </p>
            )}

            {taxEnabled && taxAmount > 0 && (
              <div
                className="flex items-center justify-between text-sm"
                style={{ color: 'var(--ef-textSecondary, #64748B)' }}
              >
                <span>{taxLabel} ({taxRate}%)</span>
                <span>{currencySymbol}{taxAmount.toFixed(2)}</span>
              </div>
            )}

            {processingFeeEnabled && passProcessingFee && feeAmount > 0 && (
              <div
                className="flex items-center justify-between text-sm"
                style={{ color: 'var(--ef-textSecondary, #64748B)' }}
              >
                <span>{t('COM_EASYFORMS_PROCESSING_FEE', 'Processing Fee')}</span>
                <span>{currencySymbol}{feeAmount.toFixed(2)}</span>
              </div>
            )}

            {(taxAmount > 0 || feeAmount > 0) && (
              <>
                <div style={{ borderTop: '1px solid var(--ef-border, #E2E8F0)', marginTop: '0.5rem', marginBottom: '0.5rem' }}></div>
                <div
                  className="flex items-center justify-between font-bold"
                  style={{ color: 'var(--ef-primary, #0F172A)' }}
                >
                  <span>{t('COM_EASYFORMS_TOTAL', 'Total')}</span>
                  <span>{currencySymbol}{totalAmount.toFixed(2)}</span>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Payment Fields - Gateway Specific */}
      <div className={cn("space-y-3", compactMode && "space-y-2")}>
        {paymentGateway === 'paypal' ? (
          /* PayPal Payment UI */
          <div className="space-y-4">
            <div className="flex flex-col items-center justify-center gap-4 p-6 rounded-lg border border-input bg-muted/30">
              {/* Official PayPal Logo */}
              <svg viewBox="0 0 124 33" className="h-10 w-auto" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M46.211 6.749h-6.839a.95.95 0 0 0-.939.802l-2.766 17.537a.57.57 0 0 0 .564.658h3.265a.95.95 0 0 0 .939-.803l.746-4.73a.95.95 0 0 1 .938-.803h2.165c4.505 0 7.105-2.18 7.784-6.5.306-1.89.013-3.375-.872-4.415-.972-1.142-2.696-1.746-4.985-1.746zM47 13.154c-.374 2.454-2.249 2.454-4.062 2.454h-1.032l.724-4.583a.57.57 0 0 1 .563-.481h.473c1.235 0 2.4 0 3.002.704.359.42.469 1.044.332 1.906zM66.654 13.075h-3.275a.57.57 0 0 0-.563.481l-.145.916-.229-.332c-.709-1.029-2.29-1.373-3.868-1.373-3.619 0-6.71 2.741-7.312 6.586-.313 1.918.132 3.752 1.22 5.031.998 1.176 2.426 1.666 4.125 1.666 2.916 0 4.533-1.875 4.533-1.875l-.146.91a.57.57 0 0 0 .562.66h2.95a.95.95 0 0 0 .939-.803l1.77-11.209a.568.568 0 0 0-.561-.658zm-4.565 6.374c-.316 1.871-1.801 3.127-3.695 3.127-.951 0-1.711-.305-2.199-.883-.484-.574-.668-1.391-.514-2.301.295-1.855 1.805-3.152 3.67-3.152.93 0 1.686.309 2.184.892.499.589.697 1.411.554 2.317zM84.096 13.075h-3.291a.954.954 0 0 0-.787.417l-4.539 6.686-1.924-6.425a.953.953 0 0 0-.912-.678h-3.234a.57.57 0 0 0-.541.754l3.625 10.638-3.408 4.811a.57.57 0 0 0 .465.9h3.287a.949.949 0 0 0 .781-.408l10.946-15.8a.57.57 0 0 0-.468-.895z" fill="#253B80"/>
                <path d="M94.992 6.749h-6.84a.95.95 0 0 0-.938.802l-2.766 17.537a.569.569 0 0 0 .562.658h3.51a.665.665 0 0 0 .656-.562l.785-4.971a.95.95 0 0 1 .938-.803h2.164c4.506 0 7.105-2.18 7.785-6.5.307-1.89.012-3.375-.873-4.415-.971-1.142-2.694-1.746-4.983-1.746zm.789 6.405c-.373 2.454-2.248 2.454-4.062 2.454h-1.031l.725-4.583a.568.568 0 0 1 .562-.481h.473c1.234 0 2.4 0 3.002.704.359.42.468 1.044.331 1.906zM115.434 13.075h-3.273a.567.567 0 0 0-.562.481l-.145.916-.23-.332c-.709-1.029-2.289-1.373-3.867-1.373-3.619 0-6.709 2.741-7.311 6.586-.312 1.918.131 3.752 1.219 5.031 1 1.176 2.426 1.666 4.125 1.666 2.916 0 4.533-1.875 4.533-1.875l-.146.91a.57.57 0 0 0 .564.66h2.949a.95.95 0 0 0 .938-.803l1.771-11.209a.571.571 0 0 0-.565-.658zm-4.565 6.374c-.314 1.871-1.801 3.127-3.695 3.127-.949 0-1.711-.305-2.199-.883-.484-.574-.666-1.391-.514-2.301.297-1.855 1.805-3.152 3.67-3.152.93 0 1.686.309 2.184.892.501.589.699 1.411.554 2.317zM119.295 7.23l-2.807 17.858a.569.569 0 0 0 .562.658h2.822c.469 0 .867-.34.939-.803l2.768-17.536a.57.57 0 0 0-.562-.659h-3.16a.571.571 0 0 0-.562.482z" fill="#179BD7"/>
                <path d="M7.266 29.154l.523-3.322-1.165-.027H1.061L4.927 1.292a.316.316 0 0 1 .314-.268h9.38c3.114 0 5.263.648 6.385 1.927.526.6.861 1.227 1.023 1.917.17.724.173 1.589.007 2.644l-.012.077v.676l.526.298a3.69 3.69 0 0 1 1.065.812c.45.513.741 1.165.864 1.938.127.795.085 1.741-.123 2.812-.24 1.232-.628 2.305-1.152 3.183a6.547 6.547 0 0 1-1.825 2c-.696.494-1.523.869-2.458 1.109-.906.236-1.939.355-3.072.355h-.73c-.522 0-1.029.188-1.427.525a2.21 2.21 0 0 0-.744 1.328l-.055.299-.924 5.855-.042.215c-.011.068-.03.102-.058.125a.155.155 0 0 1-.096.035H7.266z" fill="#253B80"/>
                <path d="M23.048 7.667c-.028.179-.06.362-.096.55-1.237 6.351-5.469 8.545-10.874 8.545H9.326c-.661 0-1.218.48-1.321 1.132L6.596 26.83l-.399 2.533a.704.704 0 0 0 .695.814h4.881c.578 0 1.069-.42 1.16-.99l.048-.248.919-5.832.059-.32c.09-.572.582-.992 1.16-.992h.73c4.729 0 8.431-1.92 9.513-7.476.452-2.321.218-4.259-.978-5.622a4.667 4.667 0 0 0-1.336-1.03z" fill="#179BD7"/>
                <path d="M21.754 7.151a9.757 9.757 0 0 0-1.203-.267 15.284 15.284 0 0 0-2.426-.177h-7.352a1.172 1.172 0 0 0-1.159.992L8.05 17.605l-.045.289a1.336 1.336 0 0 1 1.321-1.132h2.752c5.405 0 9.637-2.195 10.874-8.545.037-.188.068-.371.096-.55a6.594 6.594 0 0 0-1.017-.429 9.045 9.045 0 0 0-.277-.087z" fill="#222D65"/>
                <path d="M9.614 7.699a1.169 1.169 0 0 1 1.159-.991h7.352c.871 0 1.684.057 2.426.177a9.757 9.757 0 0 1 1.481.353c.365.121.704.264 1.017.429.368-2.347-.003-3.945-1.272-5.392C20.378.682 17.853 0 14.622 0h-9.38c-.66 0-1.223.48-1.325 1.133L.01 25.898a.806.806 0 0 0 .795.932h5.791l1.454-9.225 1.564-9.906z" fill="#253B80"/>
              </svg>

              {/* Payment Method Label */}
              <div className="text-center space-y-2">
                <p className="text-lg font-semibold text-foreground">{t('COM_EASYFORMS_PAYMENT_METHOD_PAYPAL', 'Payment Method: PayPal')}</p>
                <p className="text-sm text-muted-foreground">
                  {t('COM_EASYFORMS_PAYPAL_SUBMIT_INFO', "Click the Submit button below to proceed with PayPal checkout")}
                </p>
              </div>

              {/* Total Amount Display */}
              <div className="w-full max-w-sm p-4 rounded-lg border border-input bg-background">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-muted-foreground">{t('COM_EASYFORMS_TOTAL_AMOUNT', 'Total Amount')}</span>
                  <span className="text-2xl font-bold text-foreground">{currencySymbol}{totalAmount.toFixed(2)}</span>
                </div>
              </div>

              {/* Info Text */}
              <div className="text-center space-y-1 max-w-md">
                <p className="text-sm text-muted-foreground">
                  {t('COM_EASYFORMS_PAYPAL_REDIRECT_INFO', "After submitting this form, you'll be redirected to PayPal to securely complete your payment.")}
                </p>
                <p className="text-xs text-muted-foreground">
                  {t('COM_EASYFORMS_PAY_WITH_PAYPAL_OR_CARD', 'You will be able to pay with your PayPal account or credit/debit card.')}
                </p>
              </div>
            </div>

            {/* Billing Information (if required) */}
            {collectBillingAddress && !compactMode && (
              <div className="pt-4 border-t">
                <h4 className="text-sm font-medium mb-3">
                  {t('COM_EASYFORMS_BILLING_INFORMATION', 'Billing Information')}
                  {requireBillingAddress && <span className="text-red-500 ml-1">*</span>}
                </h4>
                <div className="space-y-3">
                  <input
                    type="text"
                    placeholder={t('COM_EASYFORMS_FULL_NAME', 'Full Name')}
                    value={cardholderName}
                    onChange={(e) => setCardholderName(e.target.value)}
                    className="w-full px-3 py-2 rounded-md border border-input bg-background"
                    required={requireBillingAddress}
                  />
                  <input
                    type="text"
                    placeholder={t('COM_EASYFORMS_STREET_ADDRESS', 'Street Address')}
                    value={billingAddress.street}
                    onChange={(e) => setBillingAddress({...billingAddress, street: e.target.value})}
                    className="w-full px-3 py-2 rounded-md border border-input bg-background"
                    required={requireBillingAddress}
                  />
                  <div className="grid grid-cols-2 gap-3">
                    <input
                      type="text"
                      placeholder={t('COM_EASYFORMS_CITY', 'City')}
                      value={billingAddress.city}
                      onChange={(e) => setBillingAddress({...billingAddress, city: e.target.value})}
                      className="px-3 py-2 rounded-md border border-input bg-background"
                      required={requireBillingAddress}
                    />
                    <input
                      type="text"
                      placeholder={t('COM_EASYFORMS_STATE_PROVINCE', 'State/Province')}
                      value={billingAddress.state}
                      onChange={(e) => setBillingAddress({...billingAddress, state: e.target.value})}
                      className="px-3 py-2 rounded-md border border-input bg-background"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <input
                      type="text"
                      placeholder={t('COM_EASYFORMS_ZIP_POSTAL_CODE', 'ZIP/Postal Code')}
                      value={billingAddress.zip}
                      onChange={(e) => setBillingAddress({...billingAddress, zip: e.target.value})}
                      className="px-3 py-2 rounded-md border border-input bg-background"
                      required={requireBillingAddress}
                    />
                    <select
                      value={billingAddress.country}
                      onChange={(e) => setBillingAddress({...billingAddress, country: e.target.value})}
                      className="px-3 py-2 rounded-md border border-input bg-background"
                      required={requireBillingAddress}
                    >
                      <option value="">{t('COM_EASYFORMS_SELECT_COUNTRY', 'Select Country')}</option>
                      {countries.map((country) => (
                        <option key={country.code} value={country.code}>
                          {country.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            )}
          </div>
        ) : (
          /* Stripe Card Payment UI */
          <>
            {/* Stripe Card Element */}
            <div>
              <label className="block text-sm font-medium mb-1">
                {t('COM_EASYFORMS_CARD_INFORMATION', 'Card Information')}
                {required && <span className="text-red-500 ml-1">*</span>}
              </label>

              {stripeInitError ? (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{stripeInitError}</AlertDescription>
                </Alert>
              ) : (
                <div className="relative">
                  <div
                    ref={cardElementRef}
                    className={cn(
                      "w-full px-3 py-3 rounded-md border bg-background min-h-[44px]",
                      stripeLoading && "opacity-50",
                      cardError ? "border-red-500" : "border-input"
                    )}
                  />
                  {stripeLoading && (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-primary border-t-transparent"></div>
                        <span>{t('COM_EASYFORMS_LOADING_PAYMENT_FORM', 'Loading payment form...')}</span>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {cardError && (
                <p className="text-sm text-red-500 mt-1 flex items-center gap-1">
                  <AlertCircle className="h-3 w-3" />
                  {cardError}
                </p>
              )}

              {!cardComplete && !cardError && !stripeLoading && (
                <p className="text-xs text-muted-foreground mt-1">
                  {t('COM_EASYFORMS_ENTER_COMPLETE_CARD_DETAILS', 'Please enter complete card details including CVV')}
                </p>
              )}
            </div>

            {/* Cardholder Name */}
            {!compactMode && (
              <div>
                <label className="block text-sm font-medium mb-1">
                  {t('COM_EASYFORMS_CARDHOLDER_NAME', 'Cardholder Name')}
                </label>
                <input
                  type="text"
                  placeholder={t('COM_EASYFORMS_FULL_NAME', 'John Doe')}
                  value={cardholderName}
                  onChange={(e) => setCardholderName(e.target.value)}
                  className="w-full px-3 py-2 rounded-md border border-input bg-background"
                />
              </div>
            )}

            {/* Billing Address */}
            {collectBillingAddress && !compactMode && (
              <div className="pt-2 border-t">
                <h4 className="text-sm font-medium mb-2">
                  {t('COM_EASYFORMS_BILLING_ADDRESS', 'Billing Address')}
                  {requireBillingAddress && <span className="text-red-500 ml-1">*</span>}
                </h4>
                <div className="space-y-2">
                  <input
                    type="text"
                    placeholder={t('COM_EASYFORMS_STREET_ADDRESS', 'Street Address')}
                    value={billingAddress.street}
                    onChange={(e) => setBillingAddress({...billingAddress, street: e.target.value})}
                    className="w-full px-3 py-2 rounded-md border border-input bg-background text-sm"
                    required={requireBillingAddress}
                  />
                  <div className="grid grid-cols-2 gap-2">
                    <input
                      type="text"
                      placeholder={t('COM_EASYFORMS_CITY', 'City')}
                      value={billingAddress.city}
                      onChange={(e) => setBillingAddress({...billingAddress, city: e.target.value})}
                      className="px-3 py-2 rounded-md border border-input bg-background text-sm"
                      required={requireBillingAddress}
                    />
                    <input
                      type="text"
                      placeholder={t('COM_EASYFORMS_STATE', 'State')}
                      value={billingAddress.state}
                      onChange={(e) => setBillingAddress({...billingAddress, state: e.target.value})}
                      className="px-3 py-2 rounded-md border border-input bg-background text-sm"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <input
                      type="text"
                      placeholder={t('COM_EASYFORMS_ZIP_CODE', 'ZIP Code')}
                      value={billingAddress.zip}
                      onChange={(e) => setBillingAddress({...billingAddress, zip: e.target.value})}
                      className="px-3 py-2 rounded-md border border-input bg-background text-sm"
                      required={requireBillingAddress}
                    />
                    <select
                      value={billingAddress.country}
                      onChange={(e) => setBillingAddress({...billingAddress, country: e.target.value})}
                      className="px-3 py-2 rounded-md border border-input bg-background text-sm"
                      required={requireBillingAddress}
                    >
                      <option value="">{t('COM_EASYFORMS_SELECT_COUNTRY', 'Select Country')}</option>
                      {countries.map((country) => (
                        <option key={country.code} value={country.code}>
                          {country.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            )}
          </>
        )}

        {/* Security Badge */}
        {showSecurityBadge && (
          <div className="flex items-center justify-center gap-2 text-xs text-muted-foreground pt-2">
            <ShieldCheck className="h-4 w-4 text-green-600" />
            <span>{t('COM_EASYFORMS_SECURE_PAYMENT_BY', 'Secure payment powered by')} {paymentGateway === 'paypal' ? 'PayPal' : paymentGateway === 'stripe' ? 'Stripe' : paymentGateway}</span>
            <Lock className="h-3 w-3" />
          </div>
        )}

        {/* Test Mode Indicator */}
        {testMode && (
          <div className="flex items-center justify-center gap-2 text-xs text-amber-600 bg-amber-50 dark:bg-amber-950/20 rounded-md px-3 py-2">
            <AlertCircle className="h-4 w-4" />
            <span>{t('COM_EASYFORMS_TEST_MODE_NO_REAL_CHARGES', 'Test Mode: No real charges will be processed')}</span>
          </div>
        )}
      </div>
    </div>
  );
};
